import type { MenuPosition } from 'common/components/Elements/Form/InputAdvancedSelection/common';
import { pageFullHeaderHeight } from 'common/utilities/browser/scroll/scroll';
import useRect from 'common/utilities/hooks/useRect/useRect';
import useViewportSize from 'common/utilities/hooks/useViewportSize/useViewportSize';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import React from 'react';
import { useSelector } from 'react-redux';

/**
 * Calculate the position of a menu that belongs to a text field.
 * Placement will be on top or bottom based on the space available.
 * Max height will be calculated within that space (from field to edge of the viewport).
 */
function useMenuPosition() {
  const [rect, wrapperRef] = useRect<HTMLDivElement>();
  const [menuPosition, setMenuPosition] = React.useState<MenuPosition>({ menuPlacement: 'bottom', maxHeight: 60 });
  const viewportSize = useViewportSize();
  const showAppHeaderEnv = useSelector((appState: ApplicationState) => appState.ui.showAppHeaderEnv);
  const topOffset = pageFullHeaderHeight(showAppHeaderEnv);

  React.useEffect(() => {
    if (rect) {
      const wrapperElement = wrapperRef.current as HTMLDivElement;
      // 24/8px as the menu is slightly above/below the element
      // 8px to give a top/bottom margin
      const bottomSpace = viewportSize.height - rect.top - wrapperElement.offsetHeight - 8 - 8;
      const topSpace = rect.top - 16 - 8;
      setMenuPosition(bottomSpace >= topSpace
        ? { menuPlacement: 'bottom', maxHeight: bottomSpace }
        : { menuPlacement: 'top', maxHeight: topSpace });
    }
  }, [topOffset, viewportSize, wrapperRef, rect]);

  return { wrapperRef, menuPosition };
}

export default useMenuPosition;
